import { useMutation } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';

/**
 * 2FA enroll/verify/backup-code mutations. secret/backupCodes 평문은
 * enroll·regenerate 응답에서 단 한 번만 노출된다. enroll·regenerate 는 아직 생성
 * OpenAPI 스펙에 없어 raw fetch 로 호출한다 (useProductCouponsQuery 와 동일 패턴).
 *
 * 두 가지 토큰 스코프에 따라 base path 가 다르다:
 * - `/admin/v1/auth/2fa/*` — login challenge 스코프 (scope=2fa-challenge accessToken).
 *   first-factor 통과 직후 강제 enrollment·verify 에 사용한다 (OtpChallengePage).
 * - `/admin/v1/me/2fa/*` — 로그인된 admin 의 self-service 스코프 (admin-access 세션
 *   토큰). MyAccountPage 의 backup code 재발급 등에 사용한다.
 * 두 path 의 request/response DTO 는 동일하며, adminFetch 가 부착하는 토큰만 다르다.
 */
const AUTH_2FA_BASE = '/admin/v1/auth/2fa';
const SELF_2FA_BASE = '/admin/v1/me/2fa';

export interface TwoFactorEnrollResult {
  secret: string;
  otpAuthUri: string;
  backupCodes: ReadonlyArray<string>;
}

export interface TwoFactorVerifyResult {
  accessToken: string;
  expiresIn: number;
  tokenType: string;
}

export interface TwoFactorBackupCodesResult {
  backupCodes: ReadonlyArray<string>;
}

const postWithIdempotency = async <T>(path: string, body?: unknown): Promise<T> => {
  const headers = { 'Idempotency-Key': crypto.randomUUID() };
  const res = await adminFetch(path, {
    method: 'POST',
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const env = (await res.json().catch(() => null)) as
    | { success?: boolean; data?: T; error?: { code?: string } }
    | null;
  if (!res.ok || env?.success === false) {
    const code = env?.error?.code ?? `HTTP_${res.status}`;
    throw new Error(code);
  }
  return (env?.data ?? {}) as T;
};

const enrollMutationOptions = (base: string) => ({
  mutationFn: async (): Promise<TwoFactorEnrollResult> => {
    const data = await postWithIdempotency<Partial<TwoFactorEnrollResult>>(`${base}/enroll`);
    return {
      secret: data.secret ?? '',
      otpAuthUri: data.otpAuthUri ?? '',
      backupCodes: data.backupCodes ?? [],
    };
  },
});

const verifyMutationOptions = (base: string) => ({
  mutationFn: ({ code }: { code: string }) =>
    postWithIdempotency<TwoFactorVerifyResult>(`${base}/verify`, { code }),
});

/**
 * 백업 코드 로그인 복구 (H-3). be-user 계약: `POST /admin/v1/auth/2fa/backup-code`
 * 는 인증 불필요(no token)이며 body `{loginId, backupCode}` 로 세션을 식별한다.
 * verify 와 동일한 `{accessToken,...}` 페이로드를 반환(1회용 소진, attempt 5 잠금).
 */
const backupCodeLoginMutationOptions = (base: string) => ({
  mutationFn: ({ loginId, backupCode }: { loginId: string; backupCode: string }) =>
    postWithIdempotency<TwoFactorVerifyResult>(`${base}/backup-code`, { loginId, backupCode }),
});

/**
 * 휴대폰 SMS 2FA 로그인 챌린지 (H-2, twoFactorMethod=PHONE). 첫 인증 통과 후
 * 2fa-challenge 토큰(adminFetch 가 부착)으로 호출. request 는 body 없음(서버가 등록된
 * 휴대폰으로 코드 발송), verify `{code}` 는 admin-access accessToken 반환.
 */
const phoneChallengeRequestMutationOptions = (base: string) => ({
  mutationFn: () => postWithIdempotency<{ maskedPhone?: string; expiresInSeconds?: number }>(
    `${base}/phone/request`,
  ),
});

const phoneChallengeVerifyMutationOptions = (base: string) => ({
  mutationFn: ({ code }: { code: string }) =>
    postWithIdempotency<TwoFactorVerifyResult>(`${base}/phone/verify`, { code }),
});

const regenerateMutationOptions = (base: string) => ({
  mutationFn: async (): Promise<TwoFactorBackupCodesResult> => {
    const data = await postWithIdempotency<Partial<TwoFactorBackupCodesResult>>(
      `${base}/backup-codes/regenerate`,
    );
    return { backupCodes: data.backupCodes ?? [] };
  },
});

// Login challenge 스코프 (2fa-challenge 토큰) — OtpChallengePage 강제 enrollment 전용.
export const useTwoFactorEnrollMutation = () =>
  useMutation<TwoFactorEnrollResult, Error>(enrollMutationOptions(AUTH_2FA_BASE));

export const useTwoFactorVerifyMutation = () =>
  useMutation<TwoFactorVerifyResult, Error, { code: string }>(verifyMutationOptions(AUTH_2FA_BASE));

export const useTwoFactorRegenerateMutation = () =>
  useMutation<TwoFactorBackupCodesResult, Error>(regenerateMutationOptions(AUTH_2FA_BASE));

export const useBackupCodeLoginMutation = () =>
  useMutation<TwoFactorVerifyResult, Error, { loginId: string; backupCode: string }>(
    backupCodeLoginMutationOptions(AUTH_2FA_BASE),
  );

export const usePhoneChallengeRequestMutation = () =>
  useMutation<{ maskedPhone?: string; expiresInSeconds?: number }, Error>(
    phoneChallengeRequestMutationOptions(AUTH_2FA_BASE),
  );

export const usePhoneChallengeVerifyMutation = () =>
  useMutation<TwoFactorVerifyResult, Error, { code: string }>(
    phoneChallengeVerifyMutationOptions(AUTH_2FA_BASE),
  );

// Self-service 스코프 (admin-access 세션 토큰) — MyAccountPage 로그인 후 2FA 관리 전용.
export const useSelfTwoFactorEnrollMutation = () =>
  useMutation<TwoFactorEnrollResult, Error>(enrollMutationOptions(SELF_2FA_BASE));

export const useSelfTwoFactorVerifyMutation = () =>
  useMutation<TwoFactorVerifyResult, Error, { code: string }>(verifyMutationOptions(SELF_2FA_BASE));

export const useSelfTwoFactorRegenerateMutation = () =>
  useMutation<TwoFactorBackupCodesResult, Error>(regenerateMutationOptions(SELF_2FA_BASE));
